<template>
  <div class="parent-onboarding-section">
    <!-- AVATAR -->
    <div class="w-100 d-flex justify-content-center">
      <ChildIcon></ChildIcon>
    </div>
    <!-- AVATAR -->

    <!-- ONBOARDING TITLE -->
    <div class="onboarding-title font-weight-bold brand_navy text-center">
      Connect your Child
    </div>

    <!-- ONBOARDING ALERT CARD -->
    <div class="alert-onboarding-card mx-auto text-center rounded-5">
      <span class="icon-error-report brand_navy font-17 mr-2"></span>
      Student code does not exist.
      <router-link
        :to="{ name: 'ParentNewChildOnboarding' }"
        class="connect-link link-underline"
        >Create an account instead</router-link
      >
    </div>

    <!-- FORM AREA -->
    <form action @submit.prevent="connectChild">
      <!-- CHILD NAME -->
      <div class="form-group">
        <label for="studentCode" class="control-label color_grey_dark">
          Student Code
          <span class="icon-info brand_tonic font-12 ml-2 pointer"></span>
        </label>
        <input
          type="text"
          v-model="form.code"
          id="studentCode"
          class="form-control"
          placeholder="Enter Student Code"
          @input="searchForChild"
        />
      </div>

      <!-- RELATIONSHIP -->
      <div class="form-group">
        <label for="relationship" class="control-label color_grey_dark"
          >Relationship</label
        >
        <select
          class="form-control"
          id="relationship"
          required
          v-model="form.relationship"
        >
          <option value selected disabled
            >- How are you related to the child? -</option
          >
          <option value="father">Father</option>
          <option value="mother">Mother</option>
          <option value="guardian">Guardian</option>
        </select>
      </div>

      <!-- CHILD CARD DISPLAY -->
      <div class="child-card-display rounded-5" v-if="child_name">
        <!-- AVATAR -->
        <div class="avatar avatar-square avatar_md brand_inverse_light_bg">
          <img v-lazy="child_image" :alt="child_name" class="avatar-img" />
        </div>

        <!-- CHILD INFO -->
        <div class="child-info">
          <div class="child-name font-weight-bold brand_primary">
            {{ child_name }}
          </div>
          <div class="child-school color_grey_dark" v-if="child_school">
            {{ child_school }} ({{ child_class }})
          </div>
          <div class="child-school color_grey_dark" v-else>
            {{ class_year }}
          </div>
        </div>
      </div>

      <!-- LINE SEPARATOR -->
      <div class="form-divider w-100"></div>

      <!-- SUBMIT BUTTON -->
      <div class="w-100 d-flex justify-content-end align-items-start mb-5">
        <button
          class="btn btn-md btn-accent font-weight-bold brand_navy"
          :disabled="isDisabled"
          ref="connectChildBtn"
        >
          Connect
        </button>
      </div>
    </form>

    <!-- MODALS -->
    <ChildConnectedModal
      v-if="child_connected"
      :child_name="child_name"
      :child_image="child_image"
      @closeTriggered="toggleConnectedChildModal"
    ></ChildConnectedModal>

    <ResponseDialog
      v-if="response_dialog"
      :alert_msg="alert_card_msg"
      :alert_type="alert_card_type"
      @closeAlert="toggleResponseDialog"
    ></ResponseDialog>
    <!-- MODALS -->
  </div>
</template>

<script>
import { mapActions } from "vuex";
import ChildIcon from "@/components/coloredIconPack/ChildIcon";

export default {
  name: "ConnectChildForm",

  components: {
    ChildIcon,
    ChildConnectedModal: () =>
      import(
        /* webpackChunkName: "ChildConnectedModal" */ "@/components/modalComps/parentModals/ChildConnectedModal"
      ),
    ResponseDialog: () =>
      import(
        /* webpackChunkName: "ResponseDialog" */ "@/components/globalComps/ResponseDialog"
      ),
  },

  data() {
    return {
      form: {
        code: "",
        relationship: "",
      },

      class_year: "",
      child_image: "",
      child_name: "",
      child_school: "",
      child_class: "",
      child_connected: false,

      alert_card_msg: "",
      alert_card_type: "",
      response_dialog: false,
    };
  },

  computed: {
    isDisabled() {
      if (this.form.relationship === "" && this.child_name === "") return true;
      else return false;
    },
  },

  methods: {
    ...mapActions(["connectExistingChild", "searchChildCode"]),

    toggleResponseDialog() {
      this.response_dialog = !this.response_dialog;
    },

    toggleConnectedChildModal() {
      this.child_connected = !this.child_connected;
    },

    // SEARCH FPR CHILD
    searchForChild() {
      this.searchChildCode(this.form.code)
        .then((response) => {
          if (response.code === 200) {
            let { data } = response;
            this.class_year = data.class.description;
            this.child_image = data.image;
            this.child_school = data.school;
            this.child_class = data.class_name;
            this.child_name = data.full_name;
          } else {
            this.class_year = "";
            this.child_image = "";
            this.child_school = "";
            this.child_class = "";
            this.child_name = "";
          }
        })
        .catch((err) => console.log(err));
    },

    // CONNECT CHILD
    connectChild() {
      this.$refs.connectChildBtn.disabled = true;
      this.$refs.connectChildBtn.innerHTML =
        '<div class="icon-dotted-roller font-17 mr-2 animate"></div>Connecting ...';

      this.connectExistingChild(this.form)
        .then((response) => {
          if (response.code === 200) {
            this.$refs.connectChildBtn.disabled = false;
            this.$refs.connectChildBtn.innerText = "Connect";

            this.toggleConnectedChildModal();

            this.form.code = "";
            this.form.relationship = "";
          } else {
            this.alert_card_msg = "You are already connected to this student";
            this.alert_card_type = "warning";
            this.toggleResponseDialog();

            setTimeout(() => {
              this.$refs.connectChildBtn.disabled = false;
              this.$refs.connectChildBtn.innerText = "Connect";
            }, 2000);
          }
        })
        .catch((err) => console.log(err));
    },
  },
};
</script>

<style lang="scss">
.parent-onboarding-section {
  .child-card-display {
    border: 1px solid $border_grey;
    transition: all 0.3s ease-in-out;
    box-sizing: border-box;
    padding: 15px;
    @include flex_row_start_nowrap;

    .avatar {
      margin-right: 15px;
    }

    .child-name {
      font-size: 16px;
      line-height: 22px;
      margin-bottom: 4px;

      @include breakpoint_max(sm) {
        font-size: 15px;
      }
    }

    .child-school {
      font-size: 14px;
      line-height: 19px;

      @include breakpoint_max(sm) {
        font-size: 13px;
      }
    }
  }

  .form-divider {
    margin: 45px auto 30px;
  }
}
</style>
